import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Wallet, Plus, Pencil } from "lucide-react";
import { api, formatApiErrorDetail, formatMXN } from "../lib/api";
import { useScope } from "../context/ScopeContext";
import ScreenHeader from "../components/layout/ScreenHeader";
import AccountCard from "../components/cards/AccountCard";
import EmptyState from "../components/EmptyState";
import EditAccountDialog from "../components/EditAccountDialog";
import AccountDetailSheet from "../components/AccountDetailSheet";
import ScopeToggle from "../components/ScopeToggle";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

export default function Cuentas() {
  const { scope } = useScope();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState({ name: "", type: "efectivo", balance: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounts", { params: { scope } });
      setAccounts(data);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const total = useMemo(
    () => accounts.reduce((acc, a) => acc + Number(a.balance || 0), 0),
    [accounts]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Ponle un nombre a tu cuenta");
      return;
    }
    setSaving(true);
    try {
      await api.post("/accounts", {
        name: form.name,
        type: form.type,
        balance: Number(form.balance || 0),
      }, { params: { scope } });
      toast.success("Cuenta creada");
      setForm({ name: "", type: "efectivo", balance: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="cuentas-screen">
      <ScreenHeader
        eyebrow={scope === "shared" ? "Cuentas compartidas" : "Tus cuentas"}
        title="Cuentas"
        subtitle={scope === "shared"
          ? "Dónde vive el dinero común. Ambos miembros las ven."
          : "Dónde vive tu dinero hoy."}
        right={
          <button
            data-testid="cuentas-add-button"
            onClick={() => setOpen(true)}
            className="h-10 px-4 rounded-full bg-sage-900 text-bone flex items-center gap-1.5 shadow-sm hover:bg-sage-900/90 active:scale-95 transition-all text-sm font-bold whitespace-nowrap"
            aria-label="Nueva cuenta"
          >
            <Plus size={16} strokeWidth={2.5} />
            <span>Nueva cuenta</span>
          </button>
        }
      />

      <div className="px-6 mb-3">
        <ScopeToggle />
      </div>

      {/* Total summary */}
      <div className="px-6 mb-5">
        <div className="rounded-3xl bg-sage-900 text-bone p-6 shadow-card relative overflow-hidden">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-bone/70">
            {scope === "shared" ? "Dinero común" : "Suma total"}
          </p>
          <p className="mt-1 font-display text-3xl font-extrabold tabular">
            {formatMXN(total)}
          </p>
          <p className="text-xs text-bone/60 mt-1">
            En {accounts.length} cuenta{accounts.length === 1 ? "" : "s"}
          </p>
        </div>
      </div>

      <div className="px-6 space-y-3">
        {loading ? (
          <p className="text-soft text-sm text-center py-8">Cargando…</p>
        ) : accounts.length === 0 ? (
          <EmptyState
            icon={Wallet}
            title={scope === "shared" ? "Sin cuentas compartidas" : "Sin cuentas todavía"}
            description={
              scope === "shared"
                ? "Crea una cuenta compartida (banco, ahorro o efectivo) para empezar a manejar el dinero común."
                : "Agrega tu efectivo, banco o tarjeta para empezar a llevar tu saldo real."
            }
            testid="cuentas-empty"
            action={
              <button
                data-testid="cuentas-empty-cta"
                onClick={() => setOpen(true)}
                className="bg-sage-900 text-bone rounded-2xl px-5 py-3 font-bold text-sm"
              >
                Crear primera cuenta
              </button>
            }
          />
        ) : (
          accounts.map((a) => (
            <div
              key={a.id}
              className="relative group"
              data-testid={`account-row-${a.id}`}
            >
              <button
                type="button"
                onClick={() => setViewing(a)}
                data-testid={`account-open-${a.id}`}
                aria-label={`Ver historial de ${a.name}`}
                className="w-full text-left block rounded-3xl focus:outline-none focus:ring-2 focus:ring-sage-500/40 active:scale-[0.995] transition-transform"
              >
                <AccountCard account={a} testid={`account-${a.id}`} />
              </button>
              <button
                type="button"
                data-testid={`account-edit-${a.id}`}
                onClick={(e) => { e.stopPropagation(); setEditing(a); }}
                aria-label="Editar cuenta"
                className="absolute right-3 top-3 h-8 w-8 rounded-full bg-white/80 backdrop-blur-sm hover:bg-sage-50 text-soft hover:text-sage-900 flex items-center justify-center transition-colors shadow-sm"
              >
                <Pencil size={14} />
              </button>
            </div>
          ))
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm bg-bone border-line rounded-3xl" data-testid="cuentas-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Nueva cuenta</DialogTitle>
            <DialogDescription className="text-soft text-sm">
              Registra una fuente de dinero.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
                Nombre
              </label>
              <input
                data-testid="account-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej. BBVA Débito"
                className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-2">
                Tipo
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "efectivo", label: "Efectivo" },
                  { key: "banco", label: "Banco" },
                  { key: "tarjeta", label: "Tarjeta" },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    data-testid={`account-type-${t.key}`}
                    onClick={() => setForm({ ...form, type: t.key })}
                    className={`py-2.5 rounded-xl text-sm font-bold border transition-colors ${
                      form.type === t.key
                        ? "bg-sage-900 text-bone border-sage-900"
                        : "bg-white border-line text-ink"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
                Saldo inicial
              </label>
              <input
                data-testid="account-balance-input"
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.balance}
                onChange={(e) => setForm({ ...form, balance: e.target.value })}
                placeholder="0.00"
                className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500"
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="account-submit-button"
              className="w-full bg-sage-900 text-bone rounded-2xl py-3 font-bold hover:bg-sage-900/90 disabled:opacity-60"
            >
              {saving ? "Guardando…" : "Crear cuenta"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <EditAccountDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        account={editing}
        onChanged={load}
      />

      <AccountDetailSheet
        account={viewing}
        onClose={() => setViewing(null)}
      />
    </div>
  );
}
